import { useCallback } from "react";
import { IEvent } from "../../interfaces/models/IEvent";
import {
  buildCalendarEventPayload,
  downloadIcsFile,
  getCalendarLinkOptions,
} from "../../utils/calendarLinks";
import { createCalendarProviderIcon } from "../../utils/calendarProviderIcons";

const DROPDOWN_CLASS = "wsg-add-to-calendar-dropdown";

export const useAddToCalendar = (event: IEvent | undefined) => {
  const handleAddToCalendar = useCallback(
    (e?: React.MouseEvent<HTMLElement>) => {
      if (!event) {
        return;
      }

      const payload = buildCalendarEventPayload(event);
      if (!payload) {
        return;
      }

      document.querySelectorAll(`.${DROPDOWN_CLASS}`).forEach((el) => el.remove());

      let x = window.innerWidth / 2;
      let y = window.innerHeight / 2;

      if (e?.currentTarget) {
        const rect = e.currentTarget.getBoundingClientRect();
        x = rect.left + rect.width / 2;
        y = rect.bottom + 8;
      }

      const dropdown = document.createElement("div");
      dropdown.className = DROPDOWN_CLASS;
      dropdown.setAttribute("role", "menu");
      dropdown.style.cssText = `
        position: fixed;
        left: ${x}px;
        top: ${y}px;
        transform: translateX(-50%);
        z-index: 100000;
        background: #ffffff;
        border: 1px solid #e5e7eb;
        border-radius: 8px;
        box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
        min-width: 220px;
        padding: 4px 0;
        font-family: Roboto, Arial, sans-serif;
      `;

      const options = getCalendarLinkOptions(payload);

      const cleanup = () => {
        dropdown.remove();
        document.removeEventListener("click", handleClickOutside);
        document.removeEventListener("touchstart", handleClickOutside);
        document.removeEventListener("keydown", handleEscape);
      };

      const handleClickOutside = (ev: Event) => {
        if (!dropdown.contains(ev.target as Node)) {
          cleanup();
        }
      };

      const handleEscape = (ev: KeyboardEvent) => {
        if (ev.key === "Escape") {
          cleanup();
        }
      };

      options.forEach((option) => {
        const item = document.createElement("button");
        item.type = "button";
        item.setAttribute("role", "menuitem");
        item.style.cssText = `
          display: flex;
          align-items: center;
          gap: 10px;
          width: 100%;
          padding: 10px 16px;
          text-align: left;
          border: none;
          background: transparent;
          cursor: pointer;
          font-size: 14px;
          line-height: 20px;
          color: #1f2937;
        `;

        const icon = createCalendarProviderIcon(option.id);
        if (icon) {
          item.appendChild(icon);
        }

        const label = document.createElement("span");
        label.textContent = option.label;
        item.appendChild(label);

        item.addEventListener("mouseenter", () => {
          item.style.background = "#f3f4f6";
        });
        item.addEventListener("mouseleave", () => {
          item.style.background = "transparent";
        });
        item.addEventListener("click", (clickEv) => {
          clickEv.stopPropagation();

          if (option.downloadIcs) {
            downloadIcsFile(payload);
          } else if (option.href) {
            window.open(option.href, "_blank", "noopener,noreferrer");
          }

          cleanup();
        });

        dropdown.appendChild(item);
      });

      document.body.appendChild(dropdown);

      setTimeout(() => {
        document.addEventListener("click", handleClickOutside);
        document.addEventListener("touchstart", handleClickOutside);
        document.addEventListener("keydown", handleEscape);
      }, 0);
    },
    [event],
  );

  return handleAddToCalendar;
};
